import { Navigate, Route, Routes } from 'react-router-dom';
import type { UserManager } from 'oidc-client-ts';
import { AuthProvider, useAuth } from './auth/AuthProvider.js';
import { Layout } from './components/Layout.js';
import { SignInPage } from './pages/SignInPage.js';
import { FtpImportPage } from './pages/FtpImportPage.js';
import { ExportsPage } from './pages/ExportsPage.js';
import { AgencyUsersPage } from './pages/AgencyUsersPage.js';
import { ExportSettingsPage } from './pages/ExportSettingsPage.js';
import { RedshiftExportPage } from './pages/RedshiftExportPage.js';
import { MoveAttachmentsPage } from './pages/MoveAttachmentsPage.js';
import { MountPage } from './pages/MountPage.js';
import { TeamPage } from './pages/TeamPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { CrmsPage } from './pages/CrmsPage.js';
import { RdsDatabasesPage } from './pages/RdsDatabasesPage.js';
import { ToolsPage } from './pages/ToolsPage.js';

function ProtectedApp() {
  const { user, userRecord, loading, signOut } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f7f5]">
        <div className="text-sm text-[#aeaaa4]">Loading…</div>
      </div>
    );
  }

  if (!user) return <SignInPage />;

  if (userRecord && !userRecord.allowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f7f5]">
        <div className="text-center max-w-sm px-6">
          <div className="w-10 h-10 rounded-full bg-[#f1f1ef] flex items-center justify-center mx-auto mb-4">
            <span className="text-lg">🔒</span>
          </div>
          <h1 className="text-sm font-semibold text-[#37352f] mb-1">Access required</h1>
          <p className="text-sm text-[#73726e]">
            You need to be added to this application by an admin.
          </p>
          <p className="text-xs text-[#aeaaa4] mt-1">{user.email}</p>
          <button
            onClick={signOut}
            className="mt-4 text-xs text-[#aeaaa4] hover:text-[#37352f] underline"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/ftp-import" replace />} />
        <Route path="/ftp-import" element={<FtpImportPage />} />
        <Route
          path="/exports"
          element={userRecord?.isAdmin ? <ExportsPage /> : <Navigate to="/ftp-import" replace />}
        />
        <Route path="/agency-users" element={<AgencyUsersPage />} />
        <Route path="/redshift-export" element={<RedshiftExportPage />} />
        <Route path="/move-attachments" element={<MoveAttachmentsPage />} />
        <Route path="/mount" element={<MountPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/tools" element={<ToolsPage />} />
        <Route path="/crms" element={<CrmsPage />} />
        <Route path="/export-settings" element={<ExportSettingsPage />} />
        <Route path="/rds-databases" element={<RdsDatabasesPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/ftp-import" replace />} />
      </Routes>
    </Layout>
  );
}

export default function App({
  userManager,
  initialAuthError = null,
}: {
  userManager: UserManager;
  initialAuthError?: string | null;
}) {
  return (
    <AuthProvider userManager={userManager} initialError={initialAuthError}>
      <ProtectedApp />
    </AuthProvider>
  );
}
